import { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useRef, useState } from 'react';
import {
  PredicateInfo,
  search,
  searchKeys,
  SearchEntityType,
  SearchMode,
  type SearchDialogsPage,
} from '@/entities/search';
import { useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage } from '@/shared/api';
import type { ConversationListItem } from '@/entities/conversations';
import { useSmartSearchState } from './useSmartSearchState';
import {
  buildSearchRequest,
  buildSearchRequestParams,
  type ConversationContentSearchOptions,
  type SearchQueryParams,
} from './buildSearchRequest';

/** Срез без запроса пользователя — им подменяется состояние поиска, когда запрос не участвует в отборе */
const NO_QUERY: SearchQueryParams = { human: '', dsl: '', searchMode: 'nl' };

/**
 * Parameters passed by Table for lazy loading
 */
export type SmartSearchLazyFetchParams = {
  limit: number;
  offset: number;
  sorting?: TableSortingState;
  filters?: TableFilterState;
};

export type UseSmartSearchLazyResult<ListItem> = {
  nlQuery: string;
  dslQuery: string;
  searchMode: SearchMode;
  setNlQuery: (query: string) => void;
  setDslQuery: (query: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  predicateInfo: PredicateInfo | null;
  isSearchLoading: boolean;
  searchError: string | null;
  fetchData: (params: SmartSearchLazyFetchParams) => Promise<{ total: number; items: ListItem[] }>;
  triggerSearch: () => void;
  clearSearch: () => void;
};

/** Extend for correct item typing per EntityType */
type MappingResult<EntityType extends SearchEntityType> = EntityType extends 'conversation'
  ? ConversationListItem
  : never;

/**
 * Smart search for Table lazy loading (fetchData).
 * Uses useSmartSearchState + queryClient.fetchQuery; cache shared with useSearchQuery.
 */
export const useSmartSearchLazy = <EntityType extends SearchEntityType>(params: {
  entityType: EntityType;
  filters?: TableFilterState;
  sorting?: TableSortingState;
  /** Restored search state from useTableState (persisted in useSettings) */
  initialSearch?: { query: string; mode: SearchMode };
  /** dialogsPage params for conversation search (attributes, mainColumns, expertReportId) */
  dialogsPage?: SearchDialogsPage;
  /** Поиск по содержимому диалога (phraseSearch/commentSearch, применяется только при entityType='conversation') */
  contentSearchOptions?: ConversationContentSearchOptions;
  /**
   * Запрос пользователя в отборе не участвует: срез строится так, будто поиск пуст. Нужно там,
   * где раздел показывает состав, заданный им самим, а не отобранный пользователем.
   * Введённый запрос при этом сохраняется — он снова начнёт действовать, когда режим снимут.
   */
  isQueryIgnored?: boolean;
}) => {
  const { entityType, initialSearch, dialogsPage, contentSearchOptions, isQueryIgnored = false } = params;

  const state = useSmartSearchState({ initialSearch });
  const { value, searchMode, clearSearch: clearSearchState, setDslQuery, setNlQuery } = state;

  const [predicateInfo, setPredicateInfo] = useState<PredicateInfo | null>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const queryRef = useRef({ ...value, searchMode });
  queryRef.current = { ...value, searchMode };

  const queryClient = useQueryClient();

  const fetchData = useCallback(
    async (fetchParams: SmartSearchLazyFetchParams): Promise<{ total: number; items: MappingResult<EntityType>[] }> => {
      const { limit, offset, sorting = [], filters = [] } = fetchParams;
      const isFirstPage = offset === 0;
      const query = isQueryIgnored ? NO_QUERY : queryRef.current;
      const currentQuery = query.searchMode === 'nl' ? query.human : query.dsl;
      const hasQuery = currentQuery.trim().length > 0;

      if (isFirstPage && hasQuery) {
        setIsSearchLoading(true);
      }
      setSearchError(null);

      try {
        const request = buildSearchRequest(entityType, query, {
          limit,
          offset,
          filters,
          sorting,
          dialogsPage,
          contentSearchOptions,
        });

        const result = await queryClient.fetchQuery({
          queryKey: searchKeys.query(request),
          queryFn: () => search<MappingResult<EntityType>>(request),
          retry: false,
        });

        // Разбор запроса возвращается в поле поиска только когда запрос и отбирал: со срезом
        // без запроса пустой предикат стёр бы введённое пользователем.
        if (isFirstPage && !isQueryIgnored) {
          setDslQuery(result.predicate?.dsl ?? '');
          result.predicate?.dslHuman && setNlQuery(result.predicate?.dslHuman);
          setPredicateInfo(result.predicate ?? null);
        }

        return { total: result.total, items: result.items };
      } catch (err) {
        // Непригодная запись условия — ответ клиентского класса с текстом проблемы:
        // его и показываем у поля, иначе причина отказа пользователю не видна
        setSearchError(getApiErrorMessage(err));
        throw err;
      } finally {
        if (isFirstPage && hasQuery) {
          setIsSearchLoading(false);
        }
      }
    },
    [entityType, queryClient, dialogsPage, contentSearchOptions, isQueryIgnored, setDslQuery, setNlQuery],
  );

  /**
   * Текущий срез выборки без постраничности — тем же путём, что и запрос списка.
   * Нужен потребителям, которым отдаётся вся выборка целиком (выгрузка файлом).
   * `dialogsPage` можно переопределить: срез обязан совпадать со списком, а состав и порядок
   * колонок у потребителя свой (выгрузка рисует колонки сама и повторяет ими таблицу).
   */
  const buildRequestParams = useCallback(
    (requestParams?: {
      filters?: TableFilterState;
      sorting?: TableSortingState;
      dialogsPage?: SearchDialogsPage;
      splitPersonColumns?: boolean;
    }) =>
      buildSearchRequestParams(entityType, isQueryIgnored ? NO_QUERY : queryRef.current, {
        ...requestParams,
        dialogsPage: requestParams?.dialogsPage ?? dialogsPage,
        contentSearchOptions,
      }),
    [entityType, dialogsPage, contentSearchOptions, isQueryIgnored],
  );

  const triggerSearch = useCallback(() => {
    // Table refetch is triggered by parent via tableKey / fetchData; no-op for API compatibility.
  }, []);

  const clearSearch = useCallback(() => {
    clearSearchState();
    setPredicateInfo(null);
    setSearchError(null);
  }, [clearSearchState]);

  return {
    ...state,
    predicateInfo,
    isSearchLoading,
    searchError,
    fetchData,
    buildRequestParams,
    triggerSearch,
    clearSearch,
  };
};
